import { getAppConfigValue } from '@elliemae/pui-app-sdk';
import {
  AttributionCategory,
  EppsCustomField,
  PricingSearchConfig,
  sampleClientLookups,
} from '@frontend/pricing-search';
import axios from 'axios';
import { LEGAL_ENTITY_UUID } from './config';

import type { MatricesResponse } from '../view/excel-parser-component/matrixTypes';

export const setClientID = (clientId: number | string) =>
  sessionStorage.setItem('clientID', clientId.toString());
export const getClientID = () => sessionStorage.getItem('clientID');
export const setClientName = (clientName: string) =>
  sessionStorage.setItem('clientName', clientName);
export const getClientName = () => sessionStorage.getItem('clientName');
export const setPPEServicesTokens = (
  token: string,
  refreshToken: string | null,
) => {
  sessionStorage.setItem('ppeServicesToken', token);
  if (refreshToken) {
    sessionStorage.setItem('ppeServicesRefreshToken', refreshToken);
  } else {
    sessionStorage.removeItem('ppeServicesRefreshToken');
  }
};
export const getPPEServicesToken = () =>
  sessionStorage.getItem('ppeServicesToken');
export const getPPEServicesRefreshToken = () =>
  sessionStorage.getItem('ppeServicesRefreshToken');

export const getRequestHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Opaque-Token': getPPEServicesToken(),
  'X-Legal-Entity': LEGAL_ENTITY_UUID,
});

export const getEPPSCustomFields = async () => {
  try {
    // Needed as that's how effective time for custom fields are set
    const date = new Date().toLocaleString('en-US', {
      timeZone: 'Atlantic/South_Georgia',
    });

    const effectiveDate = new Date(date).toISOString();

    // add a day
    const response = await axios.get(
      `${getAppConfigValue<string>(
        'serviceEndpoints.ppeServices',
      )}/client/${getClientID()}/custom-fields`,
      {
        headers: getRequestHeaders(),
        params: {
          effectiveDate,
        },
      },
    );

    return response.data.customFields as EppsCustomField[];
  } catch {
    return getEPPSCustomFields();
  }
};

export const getClientSchema = async () => {
  const response = await axios.get(
    `${getAppConfigValue<string>(
      'serviceEndpoints.ppeServices',
    )}/clients/${getClientID()}/pricing/search-config`,
    {
      headers: getRequestHeaders(),
    },
  );

  const configs = response.data.result as PricingSearchConfig[];
  return Array.isArray(configs) ? configs : [];
};

export const saveClientSchema = async (configs: PricingSearchConfig[]) => {
  const saveAction = () =>
    axios.put(
      `${getAppConfigValue<string>(
        'serviceEndpoints.ppeServices',
      )}/clients/${getClientID()}/pricing/search-config`,
      configs,
      {
        headers: getRequestHeaders(),
      },
    );
  const response = await saveAction();

  return response.data as PricingSearchConfig[];
};

let matricesCache: { id: string; data: MatricesResponse } | null = null;
let matricesInFlight: {
  id: string;
  promise: Promise<MatricesResponse>;
} | null = null;

export const getMatrices = async (rateDocId: string) => {
  if (matricesCache && matricesCache.id === rateDocId) {
    return matricesCache.data;
  }

  if (matricesInFlight && matricesInFlight.id === rateDocId) {
    return matricesInFlight.promise;
  }

  const promise = axios
    .get(
      `${getAppConfigValue<string>(
        'serviceEndpoints.ppeServices',
      )}/matrices/${rateDocId}`,
      {
        headers: getRequestHeaders(),
      },
    )
    .then((response) => {
      const data = response.data as MatricesResponse;
      matricesCache = { id: rateDocId, data };
      matricesInFlight = null;
      return data;
    })
    .catch((err) => {
      matricesInFlight = null;
      throw err;
    });

  matricesInFlight = { id: rateDocId, promise };
  return promise;
};

export const getAllLookups = async () => {
  const response = await axios.get(
    `${getAppConfigValue<string>('serviceEndpoints.ppeServices')}/lookup/all`,
    {
      headers: getRequestHeaders(),
    },
  );

  const lookups = response.data as typeof sampleClientLookups;
  return lookups;
};
